/**
 * @file LoginPage.js
 * @description Login/Register/Forgot/Reset with dynamic card transformation.
 */
import React, { useState, useEffect, useRef } from 'react';
import './LoginPage.css';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import alekhineLogo from './assets/image/loading1.gif'; // adjust if needed
import { appCheck } from '../firebase';
import { getToken } from 'firebase/app-check';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  // New state for Reset Password mode
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');

  // NEW: Terms and Conditions State
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [gisReady, setGisReady] = useState(false);

  // --- Auth Modes: 'login', 'register', 'forgot', 'reset' ---
  const [authMode, setAuthMode] = useState('login');

  // NEW: State for responsive Google Button Scaling
  const [googleScale, setGoogleScale] = useState(1);
  const GOOGLE_BASE_WIDTH = 280; // Widen to 280px to guarantee text fits
  const GOOGLE_BASE_HEIGHT = 40; // Standard Large button height

  const googleBtnRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Helper flags
  const isLogin = authMode === 'login';
  const isRegister = authMode === 'register';
  const isForgot = authMode === 'forgot';
  const isReset = authMode === 'reset';

  // --- Effect: Check Route for Reset Mode ---
  useEffect(() => {
    // If coming from magic link or manual nav
    const params = new URLSearchParams(location.search);
    const t = params.get('token');
    const uid = params.get('userId');

    // 1. Existing Token/UID Login logic (Magic Link)
    if (t && uid) {
      localStorage.setItem('token', t);
      localStorage.setItem('userId', uid);
      localStorage.setItem('userStatus', 'waitlisted');
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
      window.dispatchEvent(new Event('auth:changed'));
      navigateToHome();
      return;
    }

    // 2. Reset Password Route Check
    if (location.pathname === '/reset-password') {
      setAuthMode('reset');
      if (t) setResetToken(t);
    }
    // If we just navigated to /login, default to login (unless we were already there)
    else if (location.pathname === '/login' && authMode === 'reset') {
      setAuthMode('login');
    }
  }, [location, authMode]);


  // --- Google Identity Services (GIS) bootstrap ---
  useEffect(() => {
    let cancelled = false;

    const loadGis = () =>
      new Promise((resolve, reject) => {
        if (window.google && window.google.accounts && window.google.accounts.id) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
        document.head.appendChild(script);
      });

    (async () => {
      try {
        await loadGis();
        if (cancelled) return;

        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        if (!clientId) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('REACT_APP_GOOGLE_CLIENT_ID is not set.');
          }
          return;
        }

        // Initialize GIS
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            const id_token = response?.credential;
            if (!id_token) {
              setError('Google sign-in failed: missing credential.');
              return;
            }
            try {
              const { data, status } = await axios.post(`${API_URL}/oauth/google/verify`, { id_token });
              if (status === 200 && data?.token && data?.user_id) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('userId', data.user_id);
                if (data.email) localStorage.setItem('userEmail', data.email);

                const statusValue = typeof data.status === 'string' ? data.status : 'active';
                localStorage.setItem('userStatus', statusValue);
                if (data.has_completed_tutorial !== undefined) {
                  localStorage.setItem('has_completed_tutorial', String(data.has_completed_tutorial));
                }

                axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
                window.dispatchEvent(new Event('auth:changed'));
                // Google Ads Tracking: "First Time" Heuristic (Simpler)
                if (window.gtag && !localStorage.getItem('google_signup_tracked')) {
                  window.gtag('event', 'conversion', {
                    'send_to': 'AW-17940068068/E1NJCLO0tvsbEOTtvupC'
                  });
                  localStorage.setItem('google_signup_tracked', 'true');
                }

                navigateToHome();
              } else {
                setError(data?.message || 'Google login failed.');
              }
            } catch (e) {
              setError(e?.response?.data?.message || e.message || 'Google login failed.');
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: true
        });

        const updateGoogleDimensions = () => {
          if (googleBtnRef.current && googleBtnRef.current.parentElement) {
            const wrapperWidth = googleBtnRef.current.parentElement.getBoundingClientRect().width;
            if (wrapperWidth > 0) {
              const scale = wrapperWidth / GOOGLE_BASE_WIDTH;
              setGoogleScale(scale * 1.05);
            }
          } else {
            const targetWidthPx = window.innerHeight * 0.167; // Match 16.7vh CSS width
            setGoogleScale((targetWidthPx / GOOGLE_BASE_WIDTH) * 1.05);
          }
        };

        updateGoogleDimensions();

        let resizeTimer;
        const handleResize = () => {
          updateGoogleDimensions();
          clearTimeout(resizeTimer);
        };
        window.addEventListener('resize', handleResize);

        setGisReady(true);
        return () => window.removeEventListener('resize', handleResize);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('GIS init failed:', e);
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // --- Re-render Google Button ---
  useEffect(() => {
    // Only render Google button in Login or Register modes
    if (!gisReady || !googleBtnRef.current || !window.google || (isForgot || isReset)) return;

    const timer = setTimeout(() => {
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        text: isRegister ? 'signup_with' : 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: GOOGLE_BASE_WIDTH
      });
    }, 50);

    return () => clearTimeout(timer);
  }, [gisReady, isRegister, isForgot, isReset, googleScale]);


  // --- Central Auth Handler (Login/Register) ---
  const handleAuth = async (action) => {
    setError('');
    setInfo('');
    try {
      const endpoint = action === 'login' ? '/login' : '/register';
      const payload = { email: username, password };
      if (action === 'register') {
        payload.terms_accepted = termsAccepted;
      }
      const { data, status } = await axios.post(`${API_URL}${endpoint}`, payload);

      if (status === 200 && action === 'login') {
        const token = data?.token || data?.access_token || null;
        if (token) {
          localStorage.setItem('token', token);
          localStorage.setItem('userId', data?.user_id || data?.userId || '');
          localStorage.setItem('userEmail', username);
          const statusValue = typeof data?.status === 'string' ? data.status : 'active';
          localStorage.setItem('userStatus', statusValue);
          if (data?.has_completed_tutorial !== undefined) {
            localStorage.setItem('has_completed_tutorial', String(data.has_completed_tutorial));
          }
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        window.dispatchEvent(new Event('auth:changed'));
        setTimeout(() => navigateToHome(), 500);
        return;
      }

      // Registration success
      if (status === 200 && action === 'register') {
        // Google Ads Conversion Tracking
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17940068068/E1NJCLO0tvsbEOTtvupC'
          });
        }
        setInfo('Registration successful. Please check your email to verify the account.');
        return;
      }

      setError(`Failed: ${data.error || 'Invalid response'}`);
    } catch (err) {
      const resp = err.response?.data;

      // Auto-Login if User Already Exists (Register -> Login fallback)
      if (action === 'register' && resp?.message === 'User already exists') {
        const loginResponse = await axios.post(`${API_URL}/login`, { email: username, password })
          .catch(e => e.response);

        if (loginResponse?.status === 200) {
          const data = loginResponse.data;
          const token = data?.token || data?.access_token || null;
          if (token) {
            localStorage.setItem('token', token);
            localStorage.setItem('userId', data?.user_id || data?.userId || '');
            localStorage.setItem('userEmail', username);
            const statusValue = typeof data?.status === 'string' ? data.status : 'active';
            localStorage.setItem('userStatus', statusValue);
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            window.dispatchEvent(new Event('auth:changed'));
            setTimeout(() => navigateToHome(), 500);
            return;
          }
        } else {
          setError('Account already exists. Login failed (incorrect password?).');
          return;
        }
      }

      if (resp?.needs_verification) {
        setInfo('Please verify your email before logging in.');
      } else {
        setError(resp?.message || err.message);
      }
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
    }
  };

  // --- Forgot Password Handler ---
  const handleForgot = async () => {
    setError('');
    setInfo('');
    if (!username) {
      setError('Please enter your email address.');
      return;
    }
    try {
      await axios.post(`${API_URL}/forgot-password`, { email: username });
      setInfo('If an account exists, a password reset link has been sent.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset link.');
    }
  };

  // --- Reset Password Handler ---
  const handleReset = async () => {
    setError('');
    setInfo('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!resetToken) {
      setError('Missing reset token. Please check your email link.');
      return;
    }
    try {
      await axios.post(`${API_URL}/reset-password`, {
        token: resetToken,
        newPassword: password
      });
      setInfo('Password has been reset successfully. Redirecting to login...');
      setTimeout(() => {
        setAuthMode('login');
        setInfo('');
        setPassword('');
        setConfirmPassword('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    try {
      const { data } = await axios.post(`${API_URL}/resend-verification`, { email: username });
      setInfo(data.message || 'Verification email sent.');
      if (data.dev_verify_link) setInfo(prev => `${prev} (Dev: ${data.dev_verify_link})`);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const navigateToHome = () => {
    navigate('/home', { replace: true });
  };

  // --- Validation Logic ---
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailValid = emailRegex.test(username.trim());
  const isPasswordValid = password.length >= 1; // Basic non-empty check
  const isPasswordStrong = password.length >= 6; // Recommendation for register/reset

  const canSubmit = (() => {
    if (isLogin) return isEmailValid && isPasswordValid;
    if (isRegister) return isEmailValid && isPasswordStrong && termsAccepted;
    if (isForgot) return isEmailValid;
    if (isReset) return isPasswordStrong && (password === confirmPassword) && !!resetToken;
    return false;
  })();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (canSubmit) {
      if (isLogin) handleAuth('login');
      else if (isRegister) handleAuth('register');
      else if (isForgot) handleForgot();
      else if (isReset) handleReset();
    }
  };

  // --- Render Helpers ---
  const getTitle = () => {
    if (isReset) return 'Set New Password';
    if (isForgot) return 'Reset Password';
    if (isRegister) return 'Sign up with Alekhine AI';
    return 'Alekhine AI';
  };

  return (
    <div className="login-bg">
      <div className="login-container-2col">
        {/* Logo and Title */}
        <img src={alekhineLogo} alt="Alekhine AI Logo" className="login-logo-dark" />
        <h2 className="login-title-dark">{getTitle()}</h2>

        <div className="login-mainbox-dark">
          {/* LEFT: Main Form */}
          <form className="login-form-dark" onSubmit={handleSubmit}>

            {/* Email Field (Hidden in Reset mode, strictly speaking, or optional context. Usually hidden) */}
            {!isReset && (
              <div className="input-group">
                <label>Email:</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required={!isReset}
                />
              </div>
            )}

            {/* Password Field (Login/Register/Reset) */}
            {(isLogin || isRegister || isReset) && (
              <div className="input-group" style={{ position: 'relative' }}>
                <label>{isReset ? 'New Password:' : 'Password:'}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />

                {/* Forgot Password Button - Only in Login Mode */}
                {isLogin && (
                  <button
                    type="button"
                    className="forgot-password-btn"
                    onClick={() => setAuthMode('forgot')}
                    title="Forgot Password?"
                  >
                    {/* Lock with Question Mark */}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                      <path d="M12 19.5v.01" />
                      <path d="M12 17c0-1.1 1.5-1.5 1.5-2.5 0-.8-.7-1.5-1.5-1.5" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Confirm Password (Reset Mode Only) */}
            {isReset && (
              <div className="input-group">
                <label>Confirm Password:</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            )}

            {/* Terms and Conditions Checkbox (Register Only) - Moved ABOVE button */}
            {isRegister && (
              <div className="input-group terms-group">
                <label className="terms-label">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="terms-checkbox"
                  />
                  <span>
                    I agree to the <a href="/#/content-policy" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
                  </span>
                </label>
              </div>
            )}

            {/* Primary Action Button */}
            <div className="button-group single-btn">
              {isRegister && (
                <button className="register-button primary-action" type="submit" disabled={!canSubmit}>
                  Create Account
                </button>
              )}

              {isLogin && (
                <button className="login-button primary-action" type="submit" disabled={!canSubmit}>
                  Login
                </button>
              )}
              {isForgot && (
                <button className="login-button primary-action" type="button" onClick={handleForgot} disabled={!canSubmit}>
                  Send Reset Link
                </button>
              )}
              {isReset && (
                <button className="login-button primary-action" type="button" onClick={handleReset} disabled={!canSubmit}>
                  Set New Password
                </button>
              )}
            </div>

            {/* Toggle Links */}
            <div className="auth-toggle-row">
              {isLogin && (
                <span>Don’t have an account? <b onClick={() => setAuthMode('register')} className="auth-toggle-link">Sign up</b></span>
              )}
              {isRegister && (
                <span>Already have an account? <b onClick={() => setAuthMode('login')} className="auth-toggle-link">Log in</b></span>
              )}
              {(isForgot || isReset) && (
                <span onClick={() => { setAuthMode('login'); navigate('/login'); }} className="auth-toggle-link">Back to Login</span>
              )}
            </div>
          </form>

          {/* Divider & Social - Only visible in Login/Register modes */}
          {(isLogin || isRegister) && (
            <>
              <div className="login-divider-dark">
                <span className="login-divider-line-dark" />
                <span className="login-divider-or-dark">OR</span>
                <span className="login-divider-line-dark" />
              </div>

              <div className="login-social-col-dark">
                {/* Google Button */}
                <div className="login-social-btn-dark google"
                  style={{
                    width: '100%', padding: 0, border: 'none', background: 'transparent',
                    overflow: 'hidden', position: 'relative', display: 'flex',
                    justifyContent: 'center', alignItems: 'center'
                  }}>
                  <span ref={googleBtnRef} style={{
                    display: 'block', width: GOOGLE_BASE_WIDTH, height: GOOGLE_BASE_HEIGHT,
                    margin: 0, transform: `scale(${googleScale})`, transformOrigin: 'center center'
                  }} />
                </div>

                {/* Apple (Disabled) */}
                <button className="login-social-btn-dark apple" type="button" disabled>
                  <span className="login-social-icon">
                    <svg width="22" height="22" viewBox="0 0 22 22"><g><path fill="#fff" d="M17.66 11.56c-.01-2.22 1.82-3.27 1.9-3.31-1.03-1.51-2.63-1.72-3.19-1.74-1.35-.14-2.63.8-3.32.8-.68 0-1.73-.78-2.85-.76-1.46.02-2.8.85-3.55 2.15-1.51 2.62-.39 6.5 1.08 8.63.71 1.02 1.56 2.17 2.68 2.13 1.08-.04 1.48-.69 2.78-.69 1.3 0 1.65.69 2.79.67 1.16-.02 1.89-1.04 2.6-2.07.82-1.18 1.16-2.32 1.17-2.38-.02-.01-2.24-.86-2.26-3.41zm-2.23-6.65c.62-.76 1.04-1.83.93-2.9-.9.04-1.99.59-2.64 1.34-.58.66-1.1 1.74-.91 2.76 1.04.08 2.12-.54 2.62-1.2z" /></g></svg>
                  </span>
                  <span className="login-social-text">{isRegister ? 'Sign up with Apple' : 'Apple Login'}</span>
                </button>

                {/* Facebook (Disabled) */}
                <button className="login-social-btn-dark facebook" type="button" disabled>
                  <span className="login-social-icon">
                    <svg width="22" height="22" viewBox="0 0 22 22"><g><path fill="#1877F3" d="M22 11c0-6.07-4.93-11-11-11S0 4.93 0 11c0 5.5 4.05 10.03 9.33 10.88v-7.7H6.5v-3.18h2.83v-2.43c0-2.8 1.67-4.33 4.23-4.33 1.22 0 2.5.22 2.5.22v2.75h-1.41c-1.39 0-1.83.87-1.83 1.77v2.02h3.11l-.5 3.18h-2.61v7.7C17.95 21.03 22 16.5 22 11z" /></g></svg>
                  </span>
                  <span className="login-social-text">{isRegister ? 'Sign up with Facebook' : 'Facebook Login'}</span>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer Info Area */}
        <div className={`login-card-footer ${!!info || !!error || (!isRegister && info.includes('verify')) ? 'active' : ''}`} role="contentinfo" aria-live="polite">
          {info && <div className="info-message">{info}</div>}
          {error && <div className="error-message">{error}</div>}

          {/* Show Resend only if we have a username entered (heuristic) or if explicitly failed verification */}
          {(info.includes('verify')) && (
            <div className="resend-row">
              <span className="resend-caption">Didn’t get the email?</span>
              <button
                type="button"
                className="resend-button"
                onClick={handleResend}
                disabled={!username}
              >
                Resend verification
              </button>
            </div>
          )}

          <div className="login-legal-links">
            <a href="/#/content-policy">Content Policy</a>
            <span className="dot">•</span>
            <a href="mailto:support@alekhineai.com">Support</a>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LoginPage;
